import React, { useEffect, useState } from 'react';

const QuestionAnswers = () => {
  const [questionsData, setQuestionsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    answerCount: 'all'
  });

  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch('https://api.elthiumhealth.com/api/admin/answers');
        
        const data = await response.json();
        
        // Directly check for the message from backend
        if (data.message === "No questions found.") {
          setQuestionsData([]);
          setFilteredData([]);
          // Set the exact message from backend
          setError(data.message);
        } else if (data && Array.isArray(data.data)) {
          setQuestionsData(data.data);
          setFilteredData(data.data);
        } else {
          setQuestionsData([]);
          setFilteredData([]);
          setError(data.message || "Unexpected response format");
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setQuestionsData([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Apply filters
  useEffect(() => {
    if (!Array.isArray(questionsData) || questionsData.length === 0) {
      setFilteredData([]);
      return;
    }

    let filtered = [...questionsData];
    
    if (filters.search) {
      filtered = filtered.filter(question => 
        question && 
        question.question && 
        typeof question.question === 'string' &&
        question.question.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    
    if (filters.answerCount === 'withAnswers') {
      filtered = filtered.filter(question => 
        question && 
        Array.isArray(question.submittedAnswers) && 
        question.submittedAnswers.length > 0
      );
    } else if (filters.answerCount === 'withoutAnswers') {
      filtered = filtered.filter(question => 
        !question || 
        !Array.isArray(question.submittedAnswers) || 
        question.submittedAnswers.length === 0
      );
    }
    
    setFilteredData(filtered);
  }, [filters, questionsData]);

  // Safe data access helper functions
  const getQuestionText = (question) => {
    return question && question.question ? question.question : 'No question text available';
  };

  const getSubmittedAnswers = (question) => {
    return question && Array.isArray(question.submittedAnswers) ? question.submittedAnswers : [];
  };

  const getAnswerCount = (question) => {
    return getSubmittedAnswers(question).length;
  };

  const getQuestionId = (question) => {
    return question && question._id ? question._id : `unknown-${Math.random()}`;
  };

  const getCreatedAt = (question) => {
    return question && question.createdAt ? question.createdAt : new Date().toISOString();
  };

  const openAnswersModal = (question) => {
    if (!question) return;
    
    setSelectedQuestion(question);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedQuestion(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Enhanced Export to CSV function with detailed user information
  const exportToCSV = () => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) return;

    const csvData = [];
    
    csvData.push(['Question ID', 'Question', 'Total Answers', 'Created Date']);
    
    filteredData.forEach(question => {
      if (!question) return;
      
      const answers = getSubmittedAnswers(question);
      const questionText = getQuestionText(question);
      const questionId = getQuestionId(question);
      const createdAt = getCreatedAt(question);

      csvData.push([
        questionId,
        `"${questionText.replace(/"/g, '""')}"`,
        answers.length,
        new Date(createdAt).toLocaleString()
      ]);
      
      if (answers.length > 0) {
        csvData.push(['', 'User Details', 'Answer', 'Submitted Date']);
        
        answers.forEach(answer => {
          if (!answer) return;
          
          const user = answer.userId || {};
          const userName = user.name || 'Anonymous';
          const userEmail = user.email || 'No Email';
          const answerText = answer.answer || 'No answer text';
          const answerCreatedAt = answer.createdAt ? new Date(answer.createdAt).toLocaleString() : 'Unknown date';

          csvData.push([
            '',
            `"${userName} (${userEmail})"`,
            `"${answerText.replace(/"/g, '""')}"`,
            answerCreatedAt
          ]);
        });
        
        csvData.push(['', '', '', '']);
      } else {
        csvData.push(['', 'No answers submitted', '', '']);
        csvData.push(['', '', '', '']);
      }
    });
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `detailed_questions_answers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Alternative bulk export - Simple format
  const exportToCSVSimple = () => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) return;

    const csvData = [];
    
    csvData.push(['Question', 'User Name', 'User Email', 'Answer', 'Answer Submitted Date', 'Question Created Date']);
    
    filteredData.forEach(question => {
      if (!question) return;
      
      const answers = getSubmittedAnswers(question);
      const questionText = getQuestionText(question);
      const questionCreatedAt = getCreatedAt(question);

      if (answers.length > 0) {
        answers.forEach(answer => {
          if (!answer) return;
          
          const user = answer.userId || {};
          const userName = user.name || 'Anonymous';
          const userEmail = user.email || 'No Email';
          const answerText = answer.answer || 'No answer text';
          const answerCreatedAt = answer.createdAt ? new Date(answer.createdAt).toLocaleString() : 'Unknown date';

          csvData.push([
            `"${questionText.replace(/"/g, '""')}"`,
            `"${userName}"`,
            `"${userEmail}"`,
            `"${answerText.replace(/"/g, '""')}"`,
            answerCreatedAt,
            new Date(questionCreatedAt).toLocaleString()
          ]);
        });
      } else {
        csvData.push([
          `"${questionText.replace(/"/g, '""')}"`,
          'No User',
          'No Email',
          'No Answer',
          'N/A',
          new Date(questionCreatedAt).toLocaleString()
        ]);
      }
    });
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `questions_answers_simple_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export single question answers to CSV with detailed user info
  const exportQuestionToCSV = (question) => {
    if (!question) return;

    const csvData = [];
    const answers = getSubmittedAnswers(question);
    const questionText = getQuestionText(question);
    const questionId = getQuestionId(question);
    const questionCreatedAt = getCreatedAt(question);
    
    csvData.push(['Question ID', 'Question', 'Total Answers', 'Question Created Date']);
    csvData.push([
      questionId,
      `"${questionText.replace(/"/g, '""')}"`,
      answers.length,
      new Date(questionCreatedAt).toLocaleString()
    ]);
    
    csvData.push([]);
    
    if (answers.length > 0) {
      csvData.push(['User Name', 'User Email', 'User ID', 'Answer', 'Submitted Date']);
      
      answers.forEach(answer => {
        if (!answer) return;
        
        const user = answer.userId || {};
        const userName = user.name || 'Anonymous';
        const userEmail = user.email || 'No Email';
        const userId = user._id || 'N/A';
        const answerText = answer.answer || 'No answer text';
        const answerCreatedAt = answer.createdAt ? new Date(answer.createdAt).toLocaleString() : 'Unknown date';

        csvData.push([
          `"${userName}"`,
          `"${userEmail}"`,
          userId,
          `"${answerText.replace(/"/g, '""')}"`,
          answerCreatedAt
        ]);
      });
    } else {
      csvData.push(['No answers submitted for this question']);
    }
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `question_${questionId}_answers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      <span className="ml-3 text-xl font-semibold">Loading questions...</span>
    </div>
  );

  // Show the exact message from backend when no questions found
  if (error === "No questions found.") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Questions & Answers</h1>
            <p className="text-gray-600">Manage and review all submitted answers</p>
          </div>

          {/* Beautiful Empty State */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="max-w-md mx-auto">
              {/* Illustration */}
              <div className="relative mb-6">
                <div className="w-32 h-32 mx-auto bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-16 h-16 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              {/* Backend Message */}
              <h2 className="text-2xl font-bold text-gray-800 mb-3">No Questions Found</h2>
              <p className="text-gray-600 mb-6 text-lg">
                {error} {/* Yaha backend ka exact message show hoga */}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-semibold flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Agar koi aur error hai to woh show karo
  if (error) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md text-center">
        <svg className="w-16 h-16 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );

  const totalQuestions = Array.isArray(questionsData) ? questionsData.length : 0;
  const displayedQuestions = Array.isArray(filteredData) ? filteredData.length : 0;
  const totalAnswers = Array.isArray(filteredData) 
    ? filteredData.reduce((sum, q) => sum + getAnswerCount(q), 0) 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Questions & Answers</h1>
          <p className="text-gray-600">Manage and review all submitted answers</p>
        </div>

        {/* Filters and Export Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Questions
                </label>
                <input
                  type="text"
                  id="search"
                  placeholder="Search questions..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                />
              </div>

              <div className="flex-1">
                <label htmlFor="answerCount" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Answers
                </label>
                <select
                  id="answerCount"
                  value={filters.answerCount}
                  onChange={(e) => handleFilterChange('answerCount', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                >
                  <option value="all">All Questions</option>
                  <option value="withAnswers">With Answers</option>
                  <option value="withoutAnswers">Without Answers</option>
                </select>
              </div>
            </div>

            <div className="w-full lg:w-auto mt-4 lg:mt-0">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={exportToCSVSimple}
                  disabled={!Array.isArray(filteredData) || filteredData.length === 0}
                  className={`flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${
                    !Array.isArray(filteredData) || filteredData.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                  }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Simple CSV
                </button>
                
                <button
                  onClick={exportToCSV}
                  disabled={!Array.isArray(filteredData) || filteredData.length === 0}
                  className={`flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${
                    !Array.isArray(filteredData) || filteredData.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg transform hover:scale-105'
                  }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Detailed CSV
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="text-sm text-gray-600">
              Showing {displayedQuestions} of {totalQuestions} questions
            </div>
            <div className="text-xs text-gray-500">
              Total Answers: {totalAnswers}
            </div>
          </div>
        </div>

        {/* Questions Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                  <th className="px-8 py-6 text-left font-semibold text-lg">Question</th>
                  <th className="px-8 py-6 text-left font-semibold text-lg">Answers Count</th>
                  <th className="px-8 py-6 text-center font-semibold text-lg">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {!Array.isArray(filteredData) || filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-8 py-12 text-center">
                      <div className="text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xl font-semibold">No questions found</p>
                        <p className="mt-2">Try adjusting your filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((question, index) => {
                    if (!question) return null;
                    
                    const answerCount = getAnswerCount(question);
                    const questionText = getQuestionText(question);
                    const questionId = getQuestionId(question);
                    const createdAt = getCreatedAt(question);

                    return (
                      <tr 
                        key={questionId} 
                        className={`hover:bg-gray-50 transition-colors duration-200 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="px-8 py-6">
                          <div className="max-w-xl">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">
                              {questionText}
                            </h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                                </svg>
                                {answerCount} responses
                              </span>
                              <span className="flex items-center text-xs bg-gray-100 px-2 py-1 rounded">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                                </svg>
                                {new Date(createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-8 py-6">
                          <div className="flex items-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              answerCount === 0 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : answerCount > 5
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-blue-100 text-blue-800'
                            }`}>
                              {answerCount} answers
                            </span>
                          </div>
                        </td>
                        
                        <td className="px-8 py-6">
                          <div className="flex justify-center space-x-3">
                            <button
                              onClick={() => openAnswersModal(question)}
                              disabled={answerCount === 0}
                              className={`inline-flex items-center justify-center p-3 rounded-xl transition-all duration-200 ${
                                answerCount === 0
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 hover:scale-105 transform'
                              }`}
                              title="View Answers"
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>

                            <button
                              onClick={() => exportQuestionToCSV(question)}
                              disabled={answerCount === 0}
                              className={`inline-flex items-center justify-center p-3 rounded-xl transition-all duration-200 ${
                                answerCount === 0
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-green-100 text-green-600 hover:bg-green-200 hover:scale-105 transform'
                              }`}
                              title="Export Answers to CSV"
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Answers Modal */}
        {isModalOpen && selectedQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-white mb-2">Question Answers</h2>
                    <p className="text-indigo-100 text-sm max-w-2xl">
                      {getQuestionText(selectedQuestion)}
                    </p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => exportQuestionToCSV(selectedQuestion)}
                      disabled={getAnswerCount(selectedQuestion) === 0}
                      className={`p-2 rounded-lg transition-colors ${
                        getAnswerCount(selectedQuestion) === 0
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-green-500 hover:bg-green-600'
                      }`}
                      title="Export to CSV"
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={closeModal}
                      className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex items-center mt-4 text-indigo-100">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                  </svg>
                  <span>{getAnswerCount(selectedQuestion)} users responded</span>
                </div>
              </div>

              <div className="p-6 max-h-96 overflow-y-auto">
                {getAnswerCount(selectedQuestion) === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-500 text-lg">No answers submitted yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getSubmittedAnswers(selectedQuestion).map((answer) => {
                      if (!answer) return null;
                      
                      const user = answer.userId || {};
                      const userName = user.name || 'Anonymous User';
                      const userEmail = user.email;
                      const answerText = answer.answer || 'No answer text';
                      const answerCreatedAt = answer.createdAt ? new Date(answer.createdAt).toLocaleString() : 'Unknown date';

                      return (
                        <div 
                          key={answer._id || `answer-${Math.random()}`} 
                          className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:border-indigo-300 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-indigo-600 font-semibold text-sm">
                                  {userName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <strong className="text-gray-800 font-medium">
                                  {userName}
                                </strong>
                                {userEmail && (
                                  <p className="text-xs text-gray-600 mt-1">{userEmail}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                  Submitted on: {answerCreatedAt}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4 border">
                            <p className="text-gray-700 leading-relaxed">{answerText}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{getAnswerCount(selectedQuestion)}</span> answers total
                    {getAnswerCount(selectedQuestion) > 0 && (
                      <span className="ml-4">
                        Unique users: {new Set(getSubmittedAnswers(selectedQuestion).map(a => a.userId?._id)).size}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => exportQuestionToCSV(selectedQuestion)}
                      disabled={getAnswerCount(selectedQuestion) === 0}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        getAnswerCount(selectedQuestion) === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={closeModal}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionAnswers;